/**
 * calculateSASU.js
 * Calcul du revenu net avant PAS pour le statut SASU (Société par Actions Simplifiée Unipersonnelle)
 * Dirigeant assimilé salarié — calcul en local via le modèle social officiel.
 */

import { evaluateMonEntreprise } from './monEntrepriseClient';
import { computeStructureCharges } from './structureCharges';

/**
 * Calcule le revenu net avant PAS pour le statut SASU
 *
 * Le dirigeant de SASU est assimilé salarié : même régime de cotisations qu'un salarié classique.
 * L'API mon-entreprise est utilisée pour obtenir salaire brut et net exacts.
 *
 * @param {number} ca_ht - Chiffre d'affaires HT mensuel
 * @param {object} charges - Charges professionnelles déductibles (champs individuels)
 * @param {string} metier - Métier exercé
 * @returns {Promise<object>} Détails complets du calcul
 */
export async function calculateSASU(ca_ht, isTTC, charges, metier = '') {
  if (!ca_ht || ca_ht < 0) {
    throw new Error('Le chiffre d\'affaires doit être un nombre positif');
  }
  if (!charges || typeof charges !== 'object') {
    throw new Error('Les charges doivent être un objet valide');
  }

  const coeff = isTTC ? 1.2 : 1;

  // ========== ÉTAPE 1 : CHARGES ET MARGES ==========
  const categories = computeStructureCharges({ charges, metier, caHT: ca_ht, coeff });

  const matieres_premieres_saisies = (charges.matieres_premieres || 0) / coeff;
  const matieres_premieres = matieres_premieres_saisies > 0
    ? matieres_premieres_saisies
    : (metier === 'Bâtiment, travaux' ? ca_ht * 0.45 : 0);
  const sous_traitance = (charges.sous_traitance || 0) / coeff;
  const marge_brute = ca_ht - matieres_premieres - sous_traitance;

  const marge_economique = Math.max(0,
    marge_brute - categories.autres_frais_structure - categories.frais_fonctionnement
  );

  // ========== ÉTAPE 2 : SALAIRE BRUT ET NET (modèle social local) ==========
  // salaire brut → salaire net avant PAS
  const [salaire_brut, salaire_net_avant_pas] = await evaluateMonEntreprise(
    ["salarié . rémunération . brut", "salarié . rémunération . net . à payer avant impôt"],
    {
      'dirigeant . rémunération . totale': {
        valeur: String(Math.max(0, marge_economique)),
        unité: '€/mois',
      },
      "entreprise . catégorie juridique": "'SAS'",
      "dirigeant . assimilé salarié": "oui"
    }
  );

  const charges_patronales = marge_economique - salaire_brut;
  const cotisations_salariales = salaire_brut - salaire_net_avant_pas;

  const arrondir = (n) => Math.round(n * 100) / 100;

  return {
    source: 'api',
    salaire_net_avant_pas: arrondir(salaire_net_avant_pas),

    ca_ht: arrondir(ca_ht),
    marge_brute: arrondir(marge_brute),
    marge_economique: arrondir(marge_economique),

    salaire_brut: arrondir(salaire_brut),
    charges_patronales: arrondir(charges_patronales),
    cotisations_salariales: arrondir(cotisations_salariales),

    categories_charges: {
      matieres_et_sous_traitance: arrondir(matieres_premieres + sous_traitance),
      frais_participation_coop: 0,
      autres_frais_structure: categories.autres_frais_structure,
      frais_fonctionnement: categories.frais_fonctionnement,
    },

    avertissement: "⚠️ Pas d'assurance chômage en SASU",

    statut: 'SASU',
    description: 'SASU - Dirigeant assimilé salarié',

    protection_sociale: {
      assurance_chomage: false,
      retraite_complete: true,
      retraite_base: true,
      retraite_complementaire: true,
      accident_travail: false,
      maladie_professionnelle: false,
      formation_professionnelle: true,
      indemnites_journalieres: true,
    },

    avantages: [
      "Protection sociale complète (sauf chômage)",
      "Patrimoine personnel protégé",
      "Possibilité d'optimisation fiscale (IS)",
      "Image professionnelle valorisante",
    ],
    inconvenients: [
      "Pas d'assurance chômage",
      "Formalités de création plus lourdes",
      "Comptabilité complexe (expert-comptable obligatoire)",
      "Charges sociales importantes (~53%)",
    ],
  };
}

export default calculateSASU;
